import {TableDataRequest} from '@fl/cmsch-fe-library';
import {Opt} from 'ts-opt';

import {Milkability} from 'api/gen/Milkability';
import {MilkabilityDetail} from 'api/gen/MilkabilityDetail';
import {PagedModelMilkability} from 'api/gen/PagedModelMilkability';

import {MilkabilityFormValues} from '../components/milkability-report-form/form-values';
import {MilkabilityLoadingId} from '../types/milkability-loading-id';

/** Action creators for the milkability Redux slice. */
export const milkabilityAction = {
    getMilkabilityReports: (tableDataRequest: Opt<TableDataRequest<Milkability>>) => ({
        type: 'milkability/GET_MILKABILITY_REPORTS',
        payload: {tableDataRequest},
    }) as const,
    getMilkabilityReportsSuccess: (tableData: Opt<PagedModelMilkability>) => ({
        type: 'milkability/GET_MILKABILITY_REPORTS_SUCCESS',
        payload: tableData,
    }) as const,
    exportMilkabilityReports: () => ({
        type: 'milkability/EXPORT_MILKABILITY_REPORTS',
    }) as const,
    deleteMilkabilityReport: (id: number, refreshMilkabilityTable: boolean) => ({
        type: 'milkability/DELETE_MILKABILITY_REPORT',
        payload: {id, refreshMilkabilityTable},
    }) as const,
    deleteMilkabilityReportSuccess: (id: number) => ({
        type: 'milkability/DELETE_MILKABILITY_REPORT_SUCCESS',
        payload: {id},
    }) as const,
    validateMilkabilityData: () => ({
        type: 'milkability/VALIDATE_MILK_DATA',
    }) as const,
    createMilkabilityReport: (
        data: MilkabilityFormValues,
        focusEarTag: (() => void) | undefined,
        editingId?: number,
    ) => ({
        type: 'milkability/CREATE_MILKABILITY_REPORT',
        payload: {data, focusEarTag, editingId},
    }) as const,
    createMilkabilityReportSuccess: (data: MilkabilityDetail) => ({
        type: 'milkability/CREATE_MILKABILITY_REPORT_SUCCESS',
        payload: {data},
    }) as const,
    resetCreatedMilkabilityReports: () => ({
        type: 'milkability/RESET_CREATED_MILKABILITY_REPORTS',
    }) as const,
    calculateApmv: () => ({
        type: 'milkability/CALCULATE_APMV',
    }) as const,
    calculateApmvSuccess: (apmv: number) => ({
        type: 'milkability/CALCULATE_APMV_SUCCESS',
        payload: {apmv},
    }) as const,
    setEditingMilkabilityReportId: (id?: number) => ({
        type: 'milkability/SET_EDITING_MILK_REPORT_ID',
        payload: {id},
    }) as const,
    setLoading: (id: MilkabilityLoadingId, loading: boolean) => ({
        type: 'milkability/SET_LOADING',
        payload: {id, loading},
    }) as const,
    loadMilkabilityReportDetail: (id: number) => ({
        type: 'milkability/LOAD_MILKABILITY_REPORT_DETAIL',
        payload: {id},
    }) as const,
};

/** Action type for fetching milkability overview rows. */
export type GetMilkabilityReportsAction = ReturnType<typeof milkabilityAction.getMilkabilityReports>;
/** Action type for storing fetched milkability overview rows. */
export type GetMilkabilityReportsSuccessAction = ReturnType<typeof milkabilityAction.getMilkabilityReportsSuccess>;
/** Action type for exporting milkability reports. */
export type ExportMilkabilityReportsAction = ReturnType<typeof milkabilityAction.exportMilkabilityReports>;
/** Action type for deleting one milkability report. */
export type DeleteMilkabilityReportAction = ReturnType<typeof milkabilityAction.deleteMilkabilityReport>;
/** Action type for removing a deleted milkability report from local state. */
export type DeleteMilkabilityReportSuccessAction = ReturnType<typeof milkabilityAction.deleteMilkabilityReportSuccess>;
/** Action type for validating milkability form data asynchronously. */
export type ValidateMilkabilityDataAction = ReturnType<typeof milkabilityAction.validateMilkabilityData>;
/** Action type for creating or updating a milkability report. */
export type CreateMilkabilityReportAction = ReturnType<typeof milkabilityAction.createMilkabilityReport>;
/** Action type for storing a created or updated milkability report. */
export type CreateMilkabilityReportSuccessAction = ReturnType<typeof milkabilityAction.createMilkabilityReportSuccess>;
/** Action type for clearing created milkability reports from local state. */
export type ResetCreatedMilkabilityReportsAction = ReturnType<typeof milkabilityAction.resetCreatedMilkabilityReports>;
/** Action type for triggering APMV recalculation. */
export type CalculateApmvAction = ReturnType<typeof milkabilityAction.calculateApmv>;
/** Action type for storing a calculated APMV value. */
export type CalculateApmvSuccessAction = ReturnType<typeof milkabilityAction.calculateApmvSuccess>;
/** Action type for changing the currently edited milkability report id. */
export type SetEditingMilkabilityReportIdAction = ReturnType<typeof milkabilityAction.setEditingMilkabilityReportId>;
/** Action type for toggling milkability loading flags. */
export type SetLoadingAction = ReturnType<typeof milkabilityAction.setLoading>;
/** Action type for loading milkability report detail for editing. */
export type LoadMilkabilityReportDetailAction = ReturnType<typeof milkabilityAction.loadMilkabilityReportDetail>;

/** Union of all milkability actions. */
export type MilkabilityAction =
    | GetMilkabilityReportsAction
    | GetMilkabilityReportsSuccessAction
    | ExportMilkabilityReportsAction
    | DeleteMilkabilityReportAction
    | DeleteMilkabilityReportSuccessAction
    | ValidateMilkabilityDataAction
    | CreateMilkabilityReportAction
    | CreateMilkabilityReportSuccessAction
    | ResetCreatedMilkabilityReportsAction
    | CalculateApmvAction
    | CalculateApmvSuccessAction
    | SetEditingMilkabilityReportIdAction
    | SetLoadingAction
    | LoadMilkabilityReportDetailAction
;
